/**
 * Composable that fetches the calendar-month "Your work" aggregates
 * from ``GET /api/usage/me`` (proxied to segment_api).
 *
 * Module-level state so multiple views can mount the sidebar without
 * re-fetching. The `loadUsage()` action is idempotent and safe to
 * call after a completed job (e.g. when the user navigates back to
 * HomeView and the just-finished job should bump the numbers).
 */
import { ref } from "vue";

export interface UsageWindow {
  label: string;             // "May 2026"
  month_start: string;       // "2026-05-01"
  videos_processed: number;
  video_duration_ms: number;
  segments_found: number;
}

export interface UsageMe {
  current: UsageWindow;
  previous: UsageWindow;
}

function isUsageWindow(value: unknown): value is UsageWindow {
  if (typeof value !== "object" || value === null) return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.label === "string" &&
    typeof o.month_start === "string" &&
    typeof o.videos_processed === "number" &&
    typeof o.video_duration_ms === "number" &&
    typeof o.segments_found === "number"
  );
}

function isUsageMe(value: unknown): value is UsageMe {
  if (typeof value !== "object" || value === null) return false;
  const o = value as Record<string, unknown>;
  return isUsageWindow(o.current) && isUsageWindow(o.previous);
}

const usage = ref<UsageMe | null>(null);
const usageError = ref<string | null>(null);
// Initial `true` so the widget shows "Loading…" between mount and
// first response — the alternative (`false`) gives a few-tick window
// where all v-if branches are false and the aside renders empty.
const usageLoading = ref(true);

async function loadUsage(): Promise<void> {
  usageLoading.value = true;
  let resp: Response;
  try {
    resp = await fetch("/api/usage/me");
  } catch (err) {
    // Network-level failure (offline, DNS, etc.) — distinguished from
    // a parse failure below. ``String(err)`` collapses helpfully here
    // because the browser produces "TypeError: Failed to fetch" or
    // similar; the error landing in usageError surfaces it to the user.
    usageError.value = `Network error: ${err instanceof Error ? err.message : String(err)}`;
    console.warn(`GET /api/usage/me failed at the network layer:`, err);
    usageLoading.value = false;
    return;
  }
  try {
    if (resp.status === 401) {
      // Session expired — App.vue listens for SIGNED_OUT_EVENT via
      // useJobs.apiFetch; we just clear local state here and surface
      // the error. (No signOut here: the sidebar isn't the right
      // surface to drive the redirect — HomeView's list fetch will
      // pick it up.)
      usage.value = null;
      usageError.value = "Session expired";
      return;
    }
    if (!resp.ok) {
      // Pull body sample for diagnostics — a logger-less SPA leans on
      // console for ops visibility. Trim to 200 chars; full bodies in
      // logs are noisy and rarely add signal beyond the first error
      // line.
      const text = await resp.text().catch(() => "<unreadable>");
      usageError.value = `Failed to load usage: ${resp.status}`;
      console.warn(
        `GET /api/usage/me ${resp.status} (content-type=${resp.headers.get(
          "content-type",
        )}): ${text.slice(0, 200)}`,
      );
      return;
    }
    let body: unknown;
    try {
      body = await resp.json();
    } catch (err) {
      // 200 but unparseable body — usually a proxy misconfiguration
      // (HTML error page) or a content-type mismatch. Distinct from
      // schema drift below.
      usageError.value = "Malformed response from server";
      console.warn(
        `GET /api/usage/me returned ${resp.status} with non-JSON body`,
        err,
      );
      return;
    }
    // Defensive shape check — guards against API contract drift or a
    // test fixture that returns the wrong JSON for this URL. Without
    // this, the sidebar would throw `Cannot read properties of
    // undefined (reading 'label')` mid-render. The console.warn is
    // the ops signal — a Sentry breadcrumb hookup belongs in the
    // SPA-wide error infrastructure (not in scope here).
    if (!isUsageMe(body)) {
      usageError.value = "Unexpected response shape";
      const sample = JSON.stringify(body).slice(0, 200);
      console.warn(`GET /api/usage/me schema mismatch — body sample: ${sample}`);
      return;
    }
    usage.value = body;
    usageError.value = null;
  } finally {
    usageLoading.value = false;
  }
}

function _resetForTests(): void {
  usage.value = null;
  usageError.value = null;
  usageLoading.value = false;
}

export function useUsage() {
  return { usage, usageError, usageLoading, loadUsage, _resetForTests };
}
